import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Eye, Pencil, Download, Send, Loader2,
  FileText, CheckCircle2, AlertTriangle,
} from "lucide-react";

const STATUS_LABEL = {
  draft_pending_review: { label: "Draft — pending review", tone: "bg-amber-100 text-amber-800" },
  reviewed:             { label: "Reviewed",               tone: "bg-blue-100 text-blue-800" },
  sent:                 { label: "Sent to client",         tone: "bg-emerald-100 text-emerald-800" },
};

/**
 * Phase UX-5 — Lawyer Document Workspace.
 *
 * Section-based editor for a generated legal document. Splits the document
 * content by detected ALL-CAPS headings, exposes each section in its own
 * textarea, and re-joins on save. Preview mode renders a read-only formatted
 * view that matches the PDF export.
 *
 * Backend mutations:
 *   PATCH /api/admin/documents/{id}    — content + status
 *   POST  /api/admin/documents/{id}/send  — simulated send
 *   GET   /api/admin/documents/{id}/pdf   — PDF download
 */
export default function DocumentEditor() {
  const { matterId, docId } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [matterCtx, setMatterCtx] = useState({ client_name: "" });
  const [sections, setSections] = useState([]);     // [{heading, body}]
  const [mode, setMode] = useState("edit");          // edit | preview
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sendForm, setSendForm] = useState({ open: false, to: "", note: "" });

  // ---- load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [docRes, matterRes] = await Promise.all([
          api.get(`/admin/documents/${docId}`),
          api.get(`/matters/${matterId}`).catch(() => ({ data: { matter: {} } })),
        ]);
        if (cancelled) return;
        setDoc(docRes.data);
        setSections(parseSections(docRes.data.content || ""));
        const matter = matterRes?.data?.matter || matterRes?.data || {};
        setMatterCtx({ client_name: matter.client_name || "" });
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [docId, matterId]);

  // ---- helpers ----
  const joined = useMemo(() => serializeSections(sections), [sections]);

  const updateSection = (idx, key, value) => {
    setSections((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
    setDirty(true);
  };

  const addSection = () => {
    setSections((prev) => [...prev, { heading: "NEW SECTION", body: "" }]);
    setDirty(true);
  };

  const removeSection = (idx) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  // ---- actions ----
  const save = async ({ statusOverride } = {}) => {
    setSaving(true);
    try {
      const body = { content: joined };
      if (statusOverride) body.status = statusOverride;
      const { data } = await api.patch(`/admin/documents/${docId}`, body);
      setDoc(data.document);
      setSections(parseSections(data.document.content || ""));
      setDirty(false);
      toast.success("Document saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const markReviewed = async () => {
    if (dirty) {
      await save({ statusOverride: "reviewed" });
    } else {
      try {
        setSaving(true);
        const { data } = await api.patch(`/admin/documents/${docId}`, { status: "reviewed" });
        setDoc(data.document);
        toast.success("Marked as reviewed.");
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setSaving(false);
      }
    }
  };

  const downloadPdf = async () => {
    try {
      if (dirty) {
        await save();
      }
      const url = `/admin/documents/${docId}/pdf`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(doc?.template_name || "document").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openSend = () => {
    setSendForm({ open: true, to: "", note: "" });
  };

  const send = async () => {
    setSending(true);
    try {
      if (dirty) await save();
      const body = {};
      if (sendForm.to.trim()) body.to_email = sendForm.to.trim();
      if (sendForm.note.trim()) body.note = sendForm.note.trim();
      const { data } = await api.post(`/admin/documents/${docId}/send`, body);
      setDoc(data.document);
      setSendForm({ open: false, to: "", note: "" });
      toast.success(`Sent to ${data.notification?.to}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "client_data_incomplete") {
        const items = (detail.missing || []).slice(0, 8).join(" · ");
        toast.error(`Client hasn't completed required details: ${items}`, { duration: 8000 });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSending(false);
    }
  };

  // ---- render ----
  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading document…
      </div>
    );
  }
  if (!doc) {
    return <div className="p-12 text-zinc-500">Document not found.</div>;
  }

  const statusMeta = STATUS_LABEL[doc.status] || { label: doc.status, tone: "bg-zinc-100 text-zinc-800" };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8" data-testid="document-editor">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <button
            onClick={() => navigate(`/admin/matters/${matterId}`)}
            className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-2"
            data-testid="back-to-matter"
          >
            <ArrowLeft className="h-3 w-3" /> Back to matter
          </button>
          <h1 className="font-serif text-3xl tracking-tight" data-testid="doc-title">
            {doc.template_name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
            <span
              className={`uppercase tracking-widest px-2 py-1 ${statusMeta.tone}`}
              data-testid="doc-status-badge"
            >
              {statusMeta.label}
            </span>
            <span>Generated {new Date(doc.created_at).toLocaleString()}</span>
            {doc.last_edited_at && (
              <span>· Last edited {new Date(doc.last_edited_at).toLocaleString()}</span>
            )}
            {doc.sent_at && doc.sent_to && (
              <span>· Sent to {doc.sent_to}</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex border border-zinc-200">
            <button
              onClick={() => setMode("edit")}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 transition-colors ${
                mode === "edit" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"
              }`}
              data-testid="mode-edit-btn"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button
              onClick={() => setMode("preview")}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 transition-colors ${
                mode === "preview" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"
              }`}
              data-testid="mode-preview-btn"
            >
              <Eye className="h-3.5 w-3.5" /> Preview
            </button>
          </div>
          <button
            onClick={() => save()}
            disabled={!dirty || saving}
            className="px-4 py-2 text-sm border border-zinc-200 hover:border-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-btn"
          >
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
          <button
            onClick={markReviewed}
            disabled={saving || doc.status === "sent"}
            className="px-4 py-2 text-sm border border-zinc-200 hover:border-zinc-900 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="mark-reviewed-btn"
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark reviewed
          </button>
          <button
            onClick={downloadPdf}
            className="px-4 py-2 text-sm border border-zinc-200 hover:border-zinc-900 inline-flex items-center gap-1.5"
            data-testid="download-pdf-btn"
          >
            <Download className="h-3.5 w-3.5" /> Download PDF
          </button>
          <button
            onClick={openSend}
            disabled={doc.status === "sent"}
            className="px-4 py-2 text-sm bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="send-btn"
          >
            <Send className="h-3.5 w-3.5" /> Send to client
          </button>
        </div>
      </div>

      {doc.status === "sent" && (
        <div className="mb-6 border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            Sent to <strong>{doc.sent_to}</strong>
            {doc.sent_at ? ` on ${new Date(doc.sent_at).toLocaleString()}` : ""}.
            Edits will reopen this document for review.
          </div>
        </div>
      )}
      {/* Body */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Outline rail */}
        <aside className="lg:col-span-3" data-testid="section-outline">
          <div className="border border-zinc-200 bg-white">
            <div className="px-4 py-3 border-b border-zinc-200 text-overline text-zinc-500">Sections</div>
            <ul className="divide-y divide-zinc-100 max-h-[420px] overflow-y-auto">
              {sections.map((s, i) => (
                <li key={i}>
                  <a
                    href={`#section-${i}`}
                    className="block px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50 truncate"
                    data-testid={`outline-item-${i}`}
                  >
                    <span className="text-zinc-400 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s.heading || "(untitled)"}
                  </a>
                </li>
              ))}
            </ul>
            {mode === "edit" && (
              <button
                onClick={addSection}
                className="w-full px-4 py-3 text-xs text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 border-t border-zinc-200 text-left"
                data-testid="add-section-btn"
              >
                + Add a section
              </button>
            )}
          </div>
        </aside>

        {/* Main canvas */}
        <main className="lg:col-span-9">
          {mode === "edit" ? (
            <div className="space-y-4" data-testid="editor-canvas">
              {sections.length === 0 && (
                <div className="border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500">
                  <FileText className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
                  This document is empty. Click "Add a section" to start.
                </div>
              )}
              {sections.map((s, i) => (
                <div key={i} id={`section-${i}`} className="border border-zinc-200 bg-white" data-testid={`section-card-${i}`}>
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-200 bg-zinc-50">
                    <span className="font-mono text-xs text-zinc-400 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <input
                      value={s.heading}
                      onChange={(e) => updateSection(i, "heading", e.target.value.toUpperCase())}
                      placeholder="SECTION HEADING"
                      className="flex-1 px-2 py-1 text-sm font-semibold uppercase tracking-wide bg-transparent outline-none border border-transparent focus:border-zinc-300"
                      data-testid={`section-heading-${i}`}
                    />
                    <button
                      onClick={() => removeSection(i)}
                      className="text-xs text-zinc-400 hover:text-red-700"
                      data-testid={`section-remove-${i}`}
                    >
                      Remove
                    </button>
                  </div>
                  <textarea
                    value={s.body}
                    onChange={(e) => updateSection(i, "body", e.target.value)}
                    rows={Math.max(4, Math.min(20, (s.body || "").split("\n").length + 1))}
                    placeholder="Section content…"
                    className="w-full p-4 text-sm leading-relaxed font-serif outline-none resize-y border-0 focus:bg-zinc-50/40"
                    data-testid={`section-body-${i}`}
                  />
                </div>
              ))}
              {dirty && (
                <div className="text-xs text-amber-700 flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3" /> Unsaved changes
                </div>
              )}
            </div>
          ) : (
            <article
              className="bg-white border border-zinc-200 shadow-sm font-serif text-zinc-900 leading-relaxed"
              data-testid="preview-canvas"
            >
              {/* Firm header — logo (top-left) + contact (top-right) with hairline rule */}
              <header className="px-10 lg:px-14 pt-10 pb-5 border-b border-zinc-200">
                <div className="flex items-start justify-between gap-6">
                  <div className="shrink-0">
                    <img
                      src={`${API}/static/firm-logo.png`}
                      alt="EH Inc Attorneys & Conveyancers"
                      className="h-20 w-auto object-contain"
                      data-testid="preview-firm-logo"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </div>
                  <div className="text-right pt-1">
                    <div className="text-[15px] font-bold tracking-tight text-zinc-900">
                      EH Inc Attorneys &amp; Conveyancers
                    </div>
                    <div className="text-[11px] tracking-wide text-zinc-500 mt-1">
                      Cape Town  ·  hello@ehinc.co.za  ·  Practice No. EHINC-2026
                    </div>
                  </div>
                </div>
              </header>

              {/* Document title section */}
              <div className="px-10 lg:px-14 pt-16 pb-3 text-center">
                <h1 className="text-[26px] font-bold uppercase tracking-wide text-zinc-900 leading-tight">
                  {doc.template_name}
                </h1>
                {matterCtx.client_name && (
                  <div className="text-base font-serif font-semibold text-zinc-900 mt-6">
                    {matterCtx.client_name}
                  </div>
                )}
                <div className="text-[11px] tracking-wide text-zinc-500 mt-2">
                  {[
                    doc.matter_id ? `Matter ref: ${doc.matter_id.slice(0, 8).toUpperCase()}` : null,
                    new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }),
                  ].filter(Boolean).join("  ·  ")}
                </div>
              </div>

              {/* Body */}
              <div className="px-10 lg:px-14 py-8">
                {(() => {
                  const startIdx = letterheadOffset(sections);
                  const body = sections.slice(startIdx);
                  let firstHeadingConsumed = false;
                  return body.map((s, i) => {
                    const isHeading = !!s.heading && !isLetterheadHeading(s.heading);
                    let suppressHeading = false;
                    if (isHeading) {
                      if (!firstHeadingConsumed || isDuplicateTitleHeading(s.heading, doc.template_name)) {
                        suppressHeading = true;
                        firstHeadingConsumed = true;
                      }
                    }
                    return (
                      <section key={i} className="mb-7">
                        {isHeading && !suppressHeading && (
                          <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] mb-3 mt-7 text-zinc-900">
                            {s.heading}
                          </h2>
                        )}
                        {reflowBody(s.body).map((p, j) => (
                          <p
                            key={j}
                            className={`text-[15px] leading-7 text-justify mb-4 ${p.isClause ? "pl-5 -indent-5" : ""}`}
                          >
                            {p.text}
                          </p>
                        ))}
                      </section>
                    );
                  });
                })()}
              </div>

              {/* Footer — fixed at bottom of card */}
              <footer className="border-t border-zinc-200 px-10 lg:px-14 py-4 text-center">
                <div className="text-[10px] tracking-widest uppercase text-zinc-400">
                  EH Inc Attorneys &amp; Conveyancers  ·  Practice No. EHINC-2026
                </div>
                <div className="text-[10px] tracking-widest uppercase text-zinc-400 mt-0.5">
                  {doc.status === "sent"
                    ? "Final · sent to client"
                    : `${(doc.status || "").replace(/_/g, " ")} — requires legal review`}
                </div>
              </footer>
            </article>
          )}
        </main>
      </div>

      {/* Send dialog */}
      {sendForm.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40 p-4" data-testid="send-dialog">
          <div className="bg-white border border-zinc-200 max-w-md w-full">
            <div className="px-6 py-4 border-b border-zinc-200">
              <div className="text-overline text-zinc-500 mb-1">Send document</div>
              <h2 className="font-serif text-2xl">Send to client</h2>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div>
                <label className="text-overline text-zinc-500 block mb-1">Recipient email</label>
                <input
                  value={sendForm.to}
                  onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                  placeholder="Leave blank to use client email on file"
                  className="w-full px-3 py-2 border border-zinc-200 outline-none focus:border-zinc-900 text-sm"
                  data-testid="send-to-input"
                />
              </div>
              <div>
                <label className="text-overline text-zinc-500 block mb-1">Note (optional)</label>
                <textarea
                  value={sendForm.note}
                  onChange={(e) => setSendForm({ ...sendForm, note: e.target.value })}
                  rows={3}
                  placeholder="Short message to include with the document…"
                  className="w-full px-3 py-2 border border-zinc-200 outline-none focus:border-zinc-900 text-sm resize-none"
                  data-testid="send-note-input"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Notifications are simulated in this build — the document will be marked as
                sent, a notification record written, and the matter timeline updated.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <button
                onClick={() => setSendForm({ open: false, to: "", note: "" })}
                className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900"
                data-testid="send-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={send}
                disabled={sending}
                className="px-4 py-2 text-sm bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="send-confirm-btn"
              >
                {sending ? "Sending…" : (<><Send className="h-3.5 w-3.5" /> Send</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ===========================================================================
// Helpers — split / serialize template body into ALL-CAPS-headed sections.
// ===========================================================================

function isAllCapsHeading(line) {
  const trimmed = (line || "").trim();
  if (trimmed.length < 4 || trimmed.length > 80) return false;
  if (trimmed !== trimmed.toUpperCase()) return false;
  if (!/[A-Z]/.test(trimmed)) return false;
  // ignore separator lines like '---'
  if (/^[-=_]+$/.test(trimmed)) return false;
  return true;
}

export function parseSections(text) {
  const lines = (text || "").split("\n");
  const sections = [];
  let current = null;
  let preamble = [];
  for (const line of lines) {
    if (isAllCapsHeading(line)) {
      if (current) {
        sections.push({
          heading: current.heading,
          body: current.body.join("\n").replace(/^\n+|\n+$/g, ""),
        });
      } else if (preamble.length) {
        sections.push({
          heading: "PREAMBLE",
          body: preamble.join("\n").replace(/^\n+|\n+$/g, ""),
        });
        preamble = [];
      }
      current = { heading: line.trim(), body: [] };
    } else if (current) {
      current.body.push(line);
    } else {
      preamble.push(line);
    }
  }
  if (current) {
    sections.push({
      heading: current.heading,
      body: current.body.join("\n").replace(/^\n+|\n+$/g, ""),
    });
  } else if (preamble.length) {
    sections.push({
      heading: "PREAMBLE",
      body: preamble.join("\n").replace(/^\n+|\n+$/g, ""),
    });
  }
  return sections;
}

export function serializeSections(sections) {
  return sections
    .map((s) => {
      const heading = (s.heading || "").trim();
      const body = (s.body || "").replace(/\n+$/g, "");
      if (!heading) return body;
      return `${heading}\n\n${body}`;
    })
    .filter(Boolean)
    .join("\n\n");
}

// ----------------------------------------------------------------------------
// Letterhead detection — the first section is the firm letterhead when its
// body starts with the firm name. Rendered specially in preview mode.
// ----------------------------------------------------------------------------

function isLetterheadHeading(heading) {
  if (!heading) return false;
  return /attorneys|inc\b/i.test(heading);
}

function isDuplicateTitleHeading(heading, docTitle) {
  if (!heading || !docTitle) return false;
  const norm = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
  let h = norm(heading);
  let t = norm(docTitle);
  for (const tail of [" DRAFT", " DRAFT NOTES"]) {
    if (t.endsWith(tail)) t = t.slice(0, -tail.length).trim();
  }
  if (!h || !t) return false;
  return h === t || t.includes(h) || h.includes(t);
}

function letterheadOffset(sections) {
  if (!sections.length) return 0;
  const first = sections[0];
  const isLetterhead =
    isLetterheadHeading(first.heading) ||
    (first.heading === "PREAMBLE" && /Attorneys|Inc/i.test(first.body || ""));
  return isLetterhead ? 1 : 0;
}

// ----------------------------------------------------------------------------
// Body reflow — convert hard-wrapped lines (max ~80 chars per the doc-gen
// templates) into proper paragraphs so CSS `text-justify` can align text
// cleanly to both margins. Mirrors the PDF's `_emit_paragraph` behaviour.
// ----------------------------------------------------------------------------

const _CLAUSE_PREFIX = /^\s*\d+(\.\d+)*\.?\s/;

export function reflowBody(text) {
  const lines = (text || "").split("\n");
  const out = [];
  let buf = [];
  let isClause = false;

  const flush = () => {
    const joined = buf.map((b) => b.trim()).filter(Boolean).join(" ");
    if (joined) out.push({ text: joined, isClause });
    buf = [];
    isClause = false;
  };

  for (const ln of lines) {
    if (!ln.trim()) { flush(); continue; }
    if (_CLAUSE_PREFIX.test(ln)) {
      flush();
      isClause = true;
      buf.push(ln.replace(/^\s+/, ""));
    } else {
      buf.push(ln);
    }
  }
  flush();
  return out;
}
